import {DestroyRef, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, distinctUntilChanged, map, merge, Observable, of, startWith, Subject, switchMap, tap, timer} from 'rxjs';
import {CompanySearchFacets} from '../../../models/company.model';
import {FilterCriteria} from '../../../models/filter.model';
import {ReferenceService} from '../../../services/enrich/reference.service';
import {EnrichConsoleDiagnosticsService} from '../../../services/enrich';

/** One instance per form: old requests are cancelled before the next debounce starts. */
@Injectable()
export class FilterFacetsService {
  private readonly reference = inject(ReferenceService);
  private readonly diagnostics = inject(EnrichConsoleDiagnosticsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly retries = new Subject<void>();
  readonly options = signal<Partial<CompanySearchFacets>>({});
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  connect(changes: Observable<unknown>, criteria: () => FilterCriteria, clientId: string,
          onOptions?: (options: CompanySearchFacets) => void): void {
    const criteriaChanges = changes.pipe(startWith(null), map(criteria), distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)));
    merge(criteriaChanges, this.retries.pipe(map(criteria))).pipe(
      tap(() => { this.loading.set(true); this.error.set(null); }),
      switchMap((current, index) => (index === 0 ? of(0) : timer(200)).pipe(
        switchMap(() => this.reference.getSearchFacets(current, clientId)),
        catchError(error => {
          this.diagnostics.reportError('enrich.filter-options.load', error);
          this.error.set('Could not update filter options. Please retry.');
          return of(null);
        }),
      )),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(options => {
      this.options.set(options || {});
      this.loading.set(false);
      if (options) onOptions?.(options);
    });
  }

  retry(): void { this.retries.next(); }
}
